import type { Competition } from '../../types/competition';

/** The two languages the sheets are written in. */
export type InstructionsLanguage = 'it' | 'en';

/** Who a sheet is for: every athlete, or a squad's team managers. One PDF each. */
export type InstructionsAudience = 'athletes' | 'teamManagers';

export const INSTRUCTIONS_AUDIENCES: InstructionsAudience[] = ['athletes', 'teamManagers'];

/**
 * Everything the instructions sheet prints, worked out before any PDF code is
 * loaded: the links and the dates are the part worth testing, and the part that
 * must not be wrong on a sheet that is printed and pinned to a wall.
 */
export interface InstructionsData {
  language: InstructionsLanguage;
  /** The circuit's name, when the competition is a stage of one. */
  trophyName: string | null;
  competitionName: string;
  /** "15 November 2026", or "14 – 15 November 2026" for a weekend. */
  dates: string;
  /** Where it is held: the venue's name and the address, as far as they are known. */
  place: string | null;
  /** Whether entries are open, about to open, or over — and until when. */
  entries: EntriesWindow;
  links: {
    /** The competition's page: where an athlete enters. */
    competition: string;
    /** The same page with the squad's entry form already open. */
    teamEntry: string;
    signIn: string;
    signUp: string;
  };
  /** When the sheet was made, printed in its footer. */
  generatedOn: string;
}

export type EntriesWindow =
  | { state: 'open'; closesAt: string | null }
  | { state: 'not_yet' }
  | { state: 'closed' };

/** `?lang=` wins; then the app's own language; Italian otherwise. */
export function instructionsLanguage(requested: string | null, current: string | undefined): InstructionsLanguage {
  const pick = (value?: string | null) => (value ?? '').slice(0, 2).toLowerCase();
  if (pick(requested) === 'en' || pick(requested) === 'it') return pick(requested) as InstructionsLanguage;
  return pick(current) === 'en' ? 'en' : 'it';
}

const LOCALES: Record<InstructionsLanguage, string> = { it: 'it-IT', en: 'en-GB' };
/** The competitions are Italian, and so is the clock their deadlines are set on. */
const TIME_ZONE = 'Europe/Rome';

function formatDay(value: string, language: InstructionsLanguage) {
  return new Date(value).toLocaleDateString(LOCALES[language], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: TIME_ZONE,
  });
}

/** One day, or the span of a weekend without repeating the month and year. */
export function formatDates(start: string | undefined, end: string | undefined, language: InstructionsLanguage) {
  if (!start) return end ? formatDay(end, language) : '';
  const first = formatDay(start, language);
  if (!end || formatDay(end, language) === first) return first;

  const startDate = new Date(start);
  const endDate = new Date(end);
  const sameMonth =
    startDate.toLocaleDateString('en', { month: 'numeric', year: 'numeric', timeZone: TIME_ZONE }) ===
    endDate.toLocaleDateString('en', { month: 'numeric', year: 'numeric', timeZone: TIME_ZONE });
  if (!sameMonth) return `${first} – ${formatDay(end, language)}`;

  const startDay = startDate.toLocaleDateString(LOCALES[language], { day: 'numeric', timeZone: TIME_ZONE });
  return `${startDay} – ${formatDay(end, language)}`;
}

/** "8 November 2026, 23:59": the hour matters on the last day. */
export function formatDeadline(value: string, language: InstructionsLanguage) {
  return new Date(value).toLocaleString(LOCALES[language], {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  });
}

/**
 * Where the entries stand, as the page's own badge reads it: open while the
 * competition is in its entry phase and the organiser's hour has not passed;
 * not yet while it is only announced; over after that.
 */
export function entriesWindow(competition: Pick<Competition, 'status' | 'entries_open' | 'entries_close_at'>): EntriesWindow {
  if (competition.entries_open) return { state: 'open', closesAt: competition.entries_close_at ?? null };
  if (competition.status === 'draft' || competition.status === 'scheduled') return { state: 'not_yet' };
  return { state: 'closed' };
}

export function buildInstructionsData(
  competition: Competition,
  origin: string,
  language: InstructionsLanguage,
  now = new Date()
): InstructionsData {
  const page = `${origin}/competitions/${competition.slug}`;
  const place = [competition.venue?.name, competition.full_address].filter(Boolean).join(' — ') || null;

  return {
    language,
    trophyName: competition.trophy?.name ?? null,
    competitionName: competition.name,
    dates: formatDates(competition.start_date, competition.end_date, language),
    place,
    entries: entriesWindow(competition),
    links: {
      competition: page,
      teamEntry: `${page}?team_entry=new`,
      signIn: `${origin}/sign-in`,
      signUp: `${origin}/sign-up`,
    },
    generatedOn: now.toLocaleDateString(LOCALES[language], {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: TIME_ZONE,
    }),
  };
}

const FILE_STEMS: Record<InstructionsLanguage, Record<InstructionsAudience, string>> = {
  it: { athletes: 'istruzioni-atleti', teamManagers: 'istruzioni-team-manager' },
  en: { athletes: 'instructions-athletes', teamManagers: 'instructions-team-managers' },
};

/** What a downloaded file is called. */
export function instructionsFileName(slug: string, language: InstructionsLanguage, audience: InstructionsAudience) {
  return `${FILE_STEMS[language][audience]}-${slug}.pdf`;
}

/** A link as it is printed: no scheme, which nobody needs to read off paper. */
export function printableLink(url: string) {
  return url.replace(/^https?:\/\//, '');
}
